import moment from "moment";
import { Session } from "ezStore";
import { DateProperty, DateUtils } from "utils/DateUtils";
import { Fields, hasOptionalFieldPermission, hasPluginPermission } from "imports/Utils";
import { ContractHours } from "../ShipEvents";
import { ShipFromQuery } from "../ShipDetails";
import { AdnCargoTankDesign, AdnCargoTankType, AdnShipType } from "../types/ship";
import { BargeType, VesselType } from "__generated__/enums";
import { ShipEventsEventReportsForPeriodFragment } from "__generated__/view/ships/graphql/ship.fragments.generated";
import { ShipSchemaInput } from "./ship.schema";
import { BOSSettings } from "types/commons";

export enum ShipTabs {
	General = "general",
	Holds = "holds",
	Tanks = "tanks",
	LayerCapacities = "layercapacities",
	Contacts = "contacts",
	Availability = "availability",
	Documents = "documents",
	TonnageCertificates = "tonnagecertificates",
	Agents = "agents",
	PreviousCargoes = "previouscargoes"
}

export const getAvailabilityHoursPerDay = (settings: BOSSettings | null): number[] => {
	return settings?.multiValues.availabilityHoursPerDay.split(";").map(n => Number(n)) || [] as number[];
};

export const getShipWeekTimeWindows = (from: Date | null, to: Date | null) => {
	const amountOfWeeks = moment(to).diff(from, "week") + 1;

	return [ ...Array(amountOfWeeks).keys() ].map(index => ({
		from: DateUtils.getTimestamp(moment(from).add(index * 7, "days")),
		to: DateUtils.getTimestamp(moment(from).add((index * 7) + 6, "days").endOf("day")),
	}));
};

export const getShipDayTimeWindows = (from: DateProperty, to: DateProperty) => {
	const amountOfDays = moment(to).diff(from, "day") + 1;

	return [ ...Array(amountOfDays).keys() ].map(index => {
		if (index === 0) {
			return {
				from: moment(from),
				to: moment.min(moment(from).endOf("day"), moment(to)),
			};
		} else if (index === amountOfDays - 1) {
			return {
				from: moment(to).startOf("day"),
				to: moment(to),
			};
		} else {
			return {
				from: moment(from).add(index, "days").startOf("day"),
				to: moment(from).add(index, "days").endOf("day"),
			};
		}
	}).map(window => ({
		from: DateUtils.getTimestamp(window.from),
		to: DateUtils.getTimestamp(window.to),
	}));
};

export const filterShipEventReports = (
	shipEventReports: ShipEventsEventReportsForPeriodFragment[],
	contractHours: ContractHours,
	satisfyOnContractHours: boolean,
) => {
	return shipEventReports.map(it => ({
		...it,
		reports: it.reports.filter(report => {
			const hours = contractHours.find(it => it.ship.id === report.ship.id)?.hours;

			if (satisfyOnContractHours) {
				return hours && (Number(hours) > report.summary.totalHours);
			} else {
				return true;
			}
		}),
	})).filter(it => it.reports.length > 0);
};

export const hasImoPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "imo");
	if (!isFieldEnabled) {
		return false;
	}

	return ship?.vesselType === VesselType.SeaShip;
};

export const hasEniPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "eni");
	if (!isFieldEnabled) {
		return false;
	}

	return ship?.vesselType === VesselType.Barge;
};

export const hasLicensePlatePermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "licensePlate");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasGmpPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "gmp");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasNiwoPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "niwo");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasCapacityTeuPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	if (ship?.bargeType === BargeType.PusherTug) {
		return false;
	}

	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "capacityTeu");
	if (!isFieldEnabled || !ship?.bargeType) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled) {
		return true;
	}

	const allowedBargeTypes = new Set([
		BargeType.ContainerShip,
	]);

	return allowedBargeTypes.has(ship.bargeType);
};

export const hasTracksPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "tracks");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasBobbyCatPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "bobbyCat");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const allowedBargeTypes = new Set([
		BargeType.DryCargo, BargeType.BulkCarrier,
	]);

	return allowedBargeTypes.has(ship.bargeType);
};

export const hasSailersPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "sailors");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasReeferConnectionPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "numReeferConnections");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.DryCargo, BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasDroplinePermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "dropline");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	return ship.bargeType === BargeType.Tanker;
};

export const hasMemoPermission = (session: Session): boolean => {
	return hasOptionalFieldPermission(session, Fields.SHIP, "memo") ?? false;
};

export const hasInsurerPermission = (session: Session): boolean => {
	return hasOptionalFieldPermission(session, Fields.SHIP, "insurer") ?? false;
};

export const hasTrussingPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "trussing");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasPushBowPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "pushBow");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasCellGuidesPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "cellGuides");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.DryCargo, BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasShuttersPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "hasShutters");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.ContainerShip, BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasShuttersTypePermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "shuttersType");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.ContainerShip, BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasDeckLoadPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "deckLoad");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasNumberOfCranesPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "numberOfCranes");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasMaxCranesOnQuaysPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "maxCranesOnQuays");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	const excludeBargeTypes = new Set([
		BargeType.Tanker,
	]);

	return !excludeBargeTypes.has(ship.bargeType);
};

export const hasClassificationSocietyPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "classificationSociety");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	return ship.bargeType === BargeType.Tanker;
};

export const hasAdnCargoTankDesignPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "adnCargoTankDesign");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	return ship.bargeType === BargeType.Tanker;
};

export const hasAdnCargoTankTypePermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "adnCargoTankType");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	return ship.bargeType === BargeType.Tanker;
};

export const hasTankCladdingTypePermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "tankCladdingType");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	return ship.bargeType === BargeType.Tanker;
};


export const hasAdnShipTypePermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "adnShipType");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	return ship.bargeType === BargeType.Tanker;
};

export const hasBoilerPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "boiler");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	return ship.bargeType === BargeType.Tanker;
};

export const hasSpillRailPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "spillRail");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	return ship.bargeType === BargeType.Tanker;
};

export const hasPumpCapacityPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "pumpCapacity");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	return ship.bargeType === BargeType.Tanker;
};

export const hasNumberOfTanksPermission = (session: Session, ship: ShipFromQuery | ShipSchemaInput): boolean => {
	const isFieldEnabled = hasOptionalFieldPermission(session, Fields.SHIP, "numberOfTanks");
	if (!isFieldEnabled) {
		return false;
	}

	const isPluginEnabled = hasPluginPermission(session, "filterShipAttributesBasedOnBargeType");
	if (!isPluginEnabled || !ship?.bargeType) {
		return true;
	}

	return ship.bargeType === BargeType.Tanker;
};

export const adnCargoTankDesign = (tankDesign: string | null | undefined): string => {
	return enumField(tankDesign, AdnCargoTankDesign);
};

export const adnCargoTankType = (tankType: string | null | undefined): string => {
	return enumField(tankType, AdnCargoTankType);
};

export const adnShipType = (shipType: string | null | undefined): string => {
	return enumField(shipType, AdnShipType);
};

const enumField = (key: string | null | undefined, enumValues: { [key: string]: string }) => {
	if (!key) {
		return "-";
	}

	const value = enumValues[key as keyof typeof enumValues];
	return `${key}. ${value}`;
};
